/**
 * RevenueWidget - Виджет доходов
 * ================================
 *
 * Показывает графики доходов от AdMon и InApps с разбивкой по платформам
 * (Android/iOS) либо суммарной выручкой (Total = AdMon + InApps, Android + iOS).
 */

import React from 'react';
import { BaseWidget, useBaseWidget } from './BaseWidget';
import { useWidgetData } from '../../hooks/useWidgetData';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { MetricsService } from '../../services/metricsService';
import { pickNet } from '../../config/platformFees';
import { formatChartDate, formatTooltipDate, getXAxisConfig } from '../../utils/chartUtils';
import { Card, CardContent } from '../ui/card';
import { ToggleGroup, ToggleGroupItem } from '../ui/toggle-group';
import { LineChart, Line, BarChart, Bar, AreaChart, Area, XAxis, YAxis, CartesianGrid } from 'recharts';
import { Tooltip } from '../charts/OrderedTooltip';
import { ResponsiveChart as ResponsiveContainer } from '../ui/responsive-chart';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Calendar,
  BarChart3,
  LineChart as LineChartIcon,
  AreaChart as AreaChartIcon,
  Layers,
  Sigma
} from 'lucide-react';
import { users as usersSpecs } from '@ggstat/chart-specs';

const BREAKDOWN_KEYS = ['admonAndroid', 'admonIos', 'inappsAndroid', 'inappsIos'];

const SPLIT_SERIES = usersSpecs.revenue.split;

const TOTAL_SERIES = usersSpecs.revenue.total;

const EMPTY_PERIOD = { admonAndroid: 0, admonIos: 0, inappsAndroid: 0, inappsIos: 0 };

/**
 * Сумма всех четырёх составляющих выручки за период
 */
function sumBreakdown(row) {
  return BREAKDOWN_KEYS.reduce((sum, key) => sum + (row[key] || 0), 0);
}

/**
 * Итоги по всем периодам: по каждому срезу отдельно + суммарная выручка
 */
function calcTotals(rows) {
  const totals = Object.fromEntries(
    BREAKDOWN_KEYS.map(key => [
      key,
      rows.reduce((sum, row) => sum + (row[key] || 0), 0)
    ])
  );

  return { totals, total: sumBreakdown(totals) };
}

/**
 * Компонент статистики
 */
function StatCard({ icon: Icon, label, value, change, trend, highlight = false }) {
  const getTrendIcon = () => {
    if (trend === 'up') return <TrendingUp className="h-4 w-4 text-green-600" />;
    if (trend === 'down') return <TrendingDown className="h-4 w-4 text-red-600" />;
    return null;
  };

  return (
    <Card className={highlight ? 'border-primary/40 bg-primary/5' : undefined}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Icon className="h-4 w-4 text-primary" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">{label}</p>
              <p className="text-xl font-bold">{MetricsService.formatCurrency(value)}</p>
            </div>
          </div>
          <div className="flex flex-col items-end">
            {getTrendIcon()}
            {change !== undefined && (
              <span className={`text-xs font-medium ${change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {change >= 0 ? '+' : ''}{change.toFixed(1)}%
              </span>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Кастомная легенда с поддержкой включения/выключения
 */
function CustomLegend({ series, enabledLines, setEnabledLines }) {
  return (
    <div className="flex justify-center gap-6 mt-4">
      {series.map(({ key, label, color }) => (
        <div
          key={key}
          onClick={() => setEnabledLines(prev => ({ ...prev, [key]: !prev[key] }))}
          className="flex items-center gap-2 cursor-pointer select-none"
          style={{ opacity: enabledLines[key] ? 1 : 0.4 }}
        >
          <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: color }} />
          <span className="text-sm">{label}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * Компонент графика доходов
 */
function RevenueChart({ data, chartType = 'line', enabledLines, setEnabledLines, groupBy, viewMode = 'split' }) {
  const isTotalMode = viewMode === 'total';
  const series = isTotalMode ? TOTAL_SERIES : SPLIT_SERIES;
  const visibleSeries = isTotalMode ? series : series.filter(({ key }) => enabledLines[key]);
  // В режиме Total ряд один — стек не нужен, иначе четыре среза складываются в столбец
  const stackId = isTotalMode ? undefined : 'revenue';

  // Получаем настройки оси X
  const xAxisConfig = getXAxisConfig(groupBy, data.length);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length > 0) {
      // Считаем общую сумму для этого периода
      const total = payload.reduce((sum, entry) => sum + (entry.value || 0), 0);

      // Форматируем дату для tooltip
      const formattedLabel = formatTooltipDate(label, groupBy);

      return (
        <div className="bg-background border rounded-lg shadow-lg p-3">
          <p className="font-medium mb-2">{formattedLabel}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {MetricsService.formatCurrency(entry.value)}
            </p>
          ))}
          {payload.length > 1 && (
            <div className="border-t mt-2 pt-2">
              <p className="text-sm font-bold">
                Total: {MetricsService.formatCurrency(total)}
              </p>
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  const axes = (
    <>
      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
      <XAxis
        dataKey="period"
        tickFormatter={(value) => formatChartDate(value, groupBy)}
        {...xAxisConfig}
      />
      <YAxis
        className="text-xs"
        tick={{ fill: 'currentColor' }}
        tickFormatter={(value) => MetricsService.formatCurrency(value)}
      />
      <Tooltip order={stackId && chartType !== 'line' ? 'stack' : 'value'} content={<CustomTooltip />} />
    </>
  );

  const renderChart = () => {
    if (chartType === 'bar') {
      return (
        <BarChart data={data}>
          {axes}
          {visibleSeries.map(({ key, label, color }) => (
            <Bar
              key={key}
              dataKey={key}
              name={label}
              fill={color}
              stackId={stackId}
              isAnimationActive={false}
            />
          ))}
        </BarChart>
      );
    }

    if (chartType === 'area') {
      return (
        <AreaChart data={data}>
          {axes}
          {visibleSeries.map(({ key, label, color }) => (
            <Area
              key={key}
              type="monotone"
              dataKey={key}
              name={label}
              stroke={color}
              fill={color}
              fillOpacity={0.6}
              stackId={stackId}
              isAnimationActive={false}
            />
          ))}
        </AreaChart>
      );
    }

    // Default: line chart
    return (
      <LineChart data={data}>
        {axes}
        {visibleSeries.map(({ key, label, color }) => (
          <Line
            key={key}
            type="monotone"
            dataKey={key}
            name={label}
            stroke={color}
            strokeWidth={2}
            dot={{ fill: color, r: 4 }}
            activeDot={{ r: 6 }}
            isAnimationActive={false}
          />
        ))}
      </LineChart>
    );
  };

  return (
    <div>
      <ResponsiveContainer width="100%" height={400}>
        {renderChart()}
      </ResponsiveContainer>
      {!isTotalMode && (
        <CustomLegend
          series={series}
          enabledLines={enabledLines}
          setEnabledLines={setEnabledLines}
        />
      )}
    </div>
  );
}

/**
 * Основной компонент виджета
 */
export function RevenueWidget() {
  // Используем localStorage для сохранения настроек
  const [groupBy, setGroupBy] = useLocalStorage('revenue-widget-groupBy', 'day');
  const [chartType, setChartType] = useLocalStorage('revenue-widget-chartType', 'line');
  const [viewMode, setViewMode] = useLocalStorage('revenue-widget-viewMode', 'split');
  const [enabledLines, setEnabledLines] = useLocalStorage('revenue-widget-enabledLines', {
    admonAndroid: true,
    admonIos: true,
    inappsAndroid: true,
    inappsIos: true
  });

  // Читаем состояние чекбокса из localStorage
  const [shouldApplyFees, setShouldApplyFees] = React.useState(() => {
    const stored = localStorage.getItem('applyPlatformFees');
    return stored === 'true';
  });

  // Слушаем изменения в localStorage (когда чекбокс меняется в Sidebar)
  React.useEffect(() => {
    const handleStorageChange = () => {
      const stored = localStorage.getItem('applyPlatformFees');
      setShouldApplyFees(stored === 'true');
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('platformFeesChanged', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('platformFeesChanged', handleStorageChange);
    };
  }, []);

  // Загружаем данные используя хук
  const widgetData = useWidgetData(
    async (filters, options) => {
      // Добавляем календарную фильтрацию для Revenue виджета
      const calendarFilters = {
        ...filters,
        dateFilterType: 'calendar'
      };

      // Загружаем данные параллельно
      const [admonResponse, inappsResponse] = await Promise.all([
        MetricsService.getAdMonByPeriod(calendarFilters, groupBy, options),
        MetricsService.getInAppsByPeriod(calendarFilters, groupBy, options)
      ]);

      // API возвращает массив напрямую (не обернутый в { data: [...] })
      const admonData = Array.isArray(admonResponse) ? admonResponse : [];
      const inappsData = Array.isArray(inappsResponse) ? inappsResponse : [];

      // Объединяем данные по периодам
      const periodsMap = new Map();

      const upsertPeriod = (period) => {
        if (!periodsMap.has(period)) {
          periodsMap.set(period, { period, ...EMPTY_PERIOD });
        }
        return periodsMap.get(period);
      };

      // Добавляем AdMon данные
      admonData.forEach(item => {
        const periodData = upsertPeriod(item.period);
        if (item.platform === 'android') {
          periodData.admonAndroid = item.revenue;
        } else if (item.platform === 'ios') {
          periodData.admonIos = item.revenue;
        }
      });

      // Добавляем InApps данные
      inappsData.forEach(item => {
        const periodData = upsertPeriod(item.period);
        // revenueNet — серверный NET строки; нет его (старый кэш) — NET = gross
        if (item.platform === 'android') {
          periodData.inappsAndroid = item.revenue;
          periodData.inappsAndroidNet = pickNet(item.revenue, item.revenueNet);
        } else if (item.platform === 'ios') {
          periodData.inappsIos = item.revenue;
          periodData.inappsIosNet = pickNet(item.revenue, item.revenueNet);
        }
      });

      // Преобразуем Map в массив, сортируем и добавляем суммарную выручку по периоду
      const data = Array.from(periodsMap.values())
        .sort((a, b) => a.period.localeCompare(b.period))
        .map(row => ({ ...row, total: sumBreakdown(row) }));

      return { data, ...calcTotals(data) };
    },
    [groupBy]
  );

  // Используем хелпер для BaseWidget
  const widgetProps = useBaseWidget(widgetData);
  let { data: revenueData } = widgetProps;

  // Чистый доход: серверные inappsAndroidNet / inappsIosNet вместо gross, AdMon не меняется
  if (shouldApplyFees && revenueData) {
    const { data } = revenueData;

    if (data && Array.isArray(data)) {
      // Суммарная выручка пересчитывается после подстановки NET
      const adjustedData = data.map(item => {
        const adjustedItem = MetricsService.applyNetRevenueToItem(item);
        return { ...adjustedItem, total: sumBreakdown(adjustedItem) };
      });

      revenueData = { data: adjustedData, ...calcTotals(adjustedData) };
    }
  }

  // Компоненты выбора настроек
  const Controls = () => (
    <div className="flex flex-wrap items-center gap-y-2 gap-x-4">
      <ToggleGroup type="single" value={groupBy} onValueChange={(value) => value && setGroupBy(value)}>
        <ToggleGroupItem value="day" aria-label="Daily">
          <Calendar className="h-4 w-4 mr-1" />
          Daily
        </ToggleGroupItem>
        <ToggleGroupItem value="week" aria-label="Weekly">
          <Calendar className="h-4 w-4 mr-1" />
          Weekly
        </ToggleGroupItem>
        <ToggleGroupItem value="month" aria-label="Monthly">
          <Calendar className="h-4 w-4 mr-1" />
          Monthly
        </ToggleGroupItem>
      </ToggleGroup>

      <div className="h-6 w-px bg-border" />

      <ToggleGroup type="single" value={viewMode} onValueChange={(value) => value && setViewMode(value)}>
        <ToggleGroupItem value="split" aria-label="Split by source and platform">
          <Layers className="h-4 w-4 mr-1" />
          Split
        </ToggleGroupItem>
        <ToggleGroupItem value="total" aria-label="Total revenue">
          <Sigma className="h-4 w-4 mr-1" />
          Total
        </ToggleGroupItem>
      </ToggleGroup>

      <div className="h-6 w-px bg-border" />

      <ToggleGroup type="single" value={chartType} onValueChange={(value) => value && setChartType(value)}>
        <ToggleGroupItem value="line" aria-label="Line Chart">
          <LineChartIcon className="h-4 w-4" />
        </ToggleGroupItem>
        <ToggleGroupItem value="bar" aria-label="Bar Chart">
          <BarChart3 className="h-4 w-4" />
        </ToggleGroupItem>
        <ToggleGroupItem value="area" aria-label="Area Chart">
          <AreaChartIcon className="h-4 w-4" />
        </ToggleGroupItem>
      </ToggleGroup>
    </div>
  );

  return (
    <BaseWidget
      {...widgetProps}
      title="Revenue Analytics"
      subtitle={revenueData?.total ? `Total: ${MetricsService.formatCurrency(revenueData.total)}` : null}
      headerAction={<Controls />}
      emptyMessage="No revenue data available"
      skeletonRows={6}
    >
      <div className="space-y-6">
        {/* Статистика */}
        {revenueData?.totals && (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <StatCard
              icon={Sigma}
              label="Total revenue"
              value={revenueData.total}
              highlight
            />
            {SPLIT_SERIES.map(({ key, label }) => (
              <StatCard
                key={key}
                icon={DollarSign}
                label={label}
                value={revenueData.totals[key]}
              />
            ))}
          </div>
        )}

        {/* График */}
        {revenueData?.data && revenueData.data.length > 0 && (
          <RevenueChart
            data={revenueData.data}
            chartType={chartType}
            enabledLines={enabledLines}
            setEnabledLines={setEnabledLines}
            groupBy={groupBy}
            viewMode={viewMode}
          />
        )}
      </div>
    </BaseWidget>
  );
}

export default RevenueWidget;
